import { Button } from 'primereact/button';
import { Column } from 'primereact/column';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { DataTable } from 'primereact/datatable';
import { Dialog } from 'primereact/dialog';
import { useCallback, useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { getItem } from '../../../../utils/localStorage';
import { makeRequest2 } from '../../../../utils/makeRequest';
import { toastify } from '../../../../utils/toast';
import {
  get_lease_penalties,
  get_lease_reminders,
  toggle_lease_penalty_status,
  toggle_lease_reminder_status
} from '../../../../utils/urls';
import Layout from '../../component/layout';
import EditPenalty from './edit_penalty';
import EditReminder from './edit_reminder';

const PenaltyReminderSelector = ({
  onPenaltySelect,
  onReminderSelect,
  selectedPenaltyId,
  selectedReminderId
}) => {
  const location = useLocation();

  const [activeTab, setActiveTab] = useState(location.state?.activeTab || 'penalties');
  const [penalties, setPenalties] = useState([]);
  const [reminders, setReminders] = useState([]);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState({});

  useEffect(() => {
    if (location.state?.activeTab) {
      setActiveTab(location.state.activeTab);
    }
  }, [location.state]);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const facilityId = await getItem("selectedFacilityId");

      if (!facilityId) {
        toastify("Facility ID not found in localStorage", "error");
        return;
      }

      try {
        const penaltiesRes = await makeRequest2(`${get_lease_penalties}/${facilityId}`, "GET");
        if (penaltiesRes.success) {
          const penaltiesData = penaltiesRes.data.data || penaltiesRes.data || [];
          setPenalties(Array.isArray(penaltiesData) ? penaltiesData : []);
        } else {
          toastify(`Penalties error: ${penaltiesRes.error}`, 'error');
        }
      } catch (penaltyError) {
        toastify(`Penalties fetch error: ${penaltyError.message}`, 'error');
      }

      try {
        const remindersRes = await makeRequest2(`${get_lease_reminders}/${facilityId}`, "GET");
        if (remindersRes.success) {
          const remindersData = remindersRes.data.data || remindersRes.data || [];
          setReminders(Array.isArray(remindersData) ? remindersData : []);
        } else {
          toastify(`Reminders error: ${remindersRes.error}`, 'error');
        }
      } catch (reminderError) {
        toastify(`Reminders fetch error: ${reminderError.message}`, 'error');
      }
    } catch (error) {
      toastify(`Error: ${error.message}`, 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleEdit = (item) => {
    setSelectedItem(item);
    setShowEditDialog(true);
  };

  const handleToggleStatus = async (item) => {
    const itemType = activeTab === 'penalties' ? 'Penalty' : 'Reminder';
    const action = item.isActive ? 'deactivate' : 'activate';

    confirmDialog({
      message: `Are you sure you want to ${action} this ${itemType.toLowerCase()}?`,
      header: `${action.charAt(0).toUpperCase() + action.slice(1)} ${itemType}`,
      icon: 'pi pi-exclamation-triangle',
      accept: async () => {
        try {
          setActionLoading(prev => ({ ...prev, [item._id]: true }));
          const facilityId = await getItem("selectedFacilityId");

          if (!facilityId) {
            toastify("Facility ID not found", "error");
            return;
          }

          const endpoint = activeTab === 'penalties' ? toggle_lease_penalty_status : toggle_lease_reminder_status;

          const response = await makeRequest2(
            `${endpoint}/${facilityId}/${item._id}`,
            "PATCH",
            { isActive: !item.isActive }
          );

          if (response.success) {
            toastify(`${itemType} ${action}d successfully`, "success");
            fetchData(); // Refresh the data
          } else {
            toastify(response.error || `Failed to ${action} ${itemType.toLowerCase()}`, 'error');
          }
        } catch (error) {
          toastify(`Error ${action}ing ${itemType.toLowerCase()}`, 'error');
        } finally {
          setActionLoading(prev => ({ ...prev, [item._id]: false }));
        }
      }
    });
  };

  const statusBodyTemplate = (rowData) => {
    return (
      <span className={`badge ${rowData.isActive ? 'bg-success' : 'bg-secondary'}`}>
        {rowData.isActive ? 'Active' : 'Inactive'}
      </span>
    );
  };

  const actionBodyTemplate = (rowData) => {
    const isSelected = activeTab === 'penalties' ?
      rowData._id === selectedPenaltyId :
      rowData._id === selectedReminderId;

    const isLoading = actionLoading[rowData._id];

    return (
      <div className="d-flex gap-2 align-items-center">
        {onPenaltySelect || onReminderSelect ? (
          <Button
            icon={isSelected ? "ti ti-check" : "ti ti-plus"}
            className={`p-button-sm ${isSelected ? 'p-button-success' : 'p-button-secondary'}`}
            tooltip={isSelected ? "Selected" : "Select"}
            onClick={() => {
              if (activeTab === 'penalties' && onPenaltySelect) {
                onPenaltySelect(rowData._id);
              } else if (activeTab === 'reminders' && onReminderSelect) {
                onReminderSelect(rowData._id);
              }
            }}
            disabled={isLoading}
          />
        ) : null}

        <Button
          icon="ti ti-edit"
          className="p-button-sm p-button-warning"
          tooltip="Edit"
          onClick={() => handleEdit(rowData)}
          disabled={isLoading}
        />

        <Button
          icon={rowData.isActive ? "ti ti-ban" : "ti ti-check"}
          className={`p-button-sm ${rowData.isActive ? 'p-button-danger' : 'p-button-success'}`}
          tooltip={rowData.isActive ? "Deactivate" : "Activate"}
          onClick={() => handleToggleStatus(rowData)}
          loading={isLoading}
          disabled={isLoading}
        />
      </div>
    );
  };

  const amountBodyTemplate = (rowData) => {
    if (rowData.type === 'percentage') {
      return `${rowData.percentage}%`;
    }
    return rowData.amount || '0';
  };

  const notificationsBodyTemplate = (rowData) => {
    if (!rowData.notificationTypes || !Array.isArray(rowData.notificationTypes)) {
      return '-';
    }
    return rowData.notificationTypes.join(', ');
  };

  return (
    <Layout>
      <ConfirmDialog />

      <div className="page-header">
        <div className="page-block">
          <div className="row align-items-center">
            <div className="col-md-12">
              <ul className="breadcrumb">
                <li className="breadcrumb-item">
                  <Link to="/facility/">Dashboard</Link>
                </li>
                <li className="breadcrumb-item">
                  <Link to="">Lease Management</Link>
                </li>
                <li className="breadcrumb-item">
                  <Link to="">Penalties and Reminders</Link>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-body py-0">
          <ul className="nav nav-tabs profile-tabs" role="tablist">
            <li className="nav-item">
              <button
                className={`nav-link ${activeTab === 'penalties' ? 'active' : ''}`}
                onClick={() => setActiveTab('penalties')}
                role="tab"
                type="button"
              >
                <i className="ti ti-money me-2"></i>Penalties
              </button>
            </li>
            <li className="nav-item">
              <button
                className={`nav-link ${activeTab === 'reminders' ? 'active' : ''}`}
                onClick={() => setActiveTab('reminders')}
                role="tab"
                type="button"
              >
                <i className="ti ti-bell me-2"></i>Reminders
              </button>
            </li>
          </ul>

          <div className="tab-content">
            <div className={`tab-pane ${activeTab === 'penalties' ? 'show active' : ''}`}>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h6 className="mb-0">Lease Penalties</h6>
                <Link to="/facility/lease_management/lease_penalties/add_penalty" className="btn btn-primary btn-sm">
                  <i className="ti ti-plus"></i> Add New Penalty
                </Link>
              </div>
              <DataTable
                value={penalties}
                emptyMessage="No penalties found."
                stripedRows
                loading={loading}
                tableStyle={{ minWidth: '50rem' }}
                sortField="name"
                sortOrder={1}
                paginator
                rows={10}
                paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                currentPageReportTemplate="Showing {first} to {last} of {totalRecords} penalties"
              >
                <Column field="name" header="Name" sortable></Column>
                <Column field="type" header="Type" sortable></Column>
                <Column
                  field="amount"
                  header="Amount/Rate"
                  body={amountBodyTemplate}
                  sortable
                ></Column>
                <Column field="effectDays" header="Effect Days" sortable></Column>
                <Column body={statusBodyTemplate} header="Status" sortable field="isActive"></Column>
                <Column body={actionBodyTemplate} header="Actions" style={{ width: '200px' }}></Column>
              </DataTable>
            </div>

            <div className={`tab-pane ${activeTab === 'reminders' ? 'show active' : ''}`}>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h6 className="mb-0">Lease Reminders</h6>
                <Link to="/facility/lease_management/lease_penalties/add_reminder" className="btn btn-primary btn-sm">
                  <i className="ti ti-plus"></i> Add New Reminder
                </Link>
              </div>
              <DataTable
                value={reminders}
                emptyMessage="No reminders found."
                stripedRows
                loading={loading}
                tableStyle={{ minWidth: '50rem' }}
                sortField="name"
                sortOrder={1}
                paginator
                rows={10}
                paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                currentPageReportTemplate="Showing {first} to {last} of {totalRecords} reminders"
              >
                <Column field="name" header="Name" sortable></Column>
                <Column field="type" header="Type" sortable></Column>
                <Column
                  field="notificationTypes"
                  header="Notifications"
                  body={notificationsBodyTemplate}
                ></Column>
                <Column field="time" header="Time" sortable></Column>
                <Column body={statusBodyTemplate} header="Status" sortable field="isActive"></Column>
                <Column body={actionBodyTemplate} header="Actions" style={{ width: '200px' }}></Column>
              </DataTable>
            </div>
          </div>
        </div>
      </div>

      <Dialog
        header={`Edit ${activeTab === 'penalties' ? 'Penalty' : 'Reminder'}`}
        visible={showEditDialog}
        style={{ width: '50vw', minWidth: '400px' }}
        modal
        onHide={() => {
          setShowEditDialog(false);
          setSelectedItem(null);
        }}
        maximizable
      >
        {activeTab === 'penalties' ? (
          <EditPenalty
            penalty={selectedItem}
            isDialog={true}
            onClose={() => {
              setShowEditDialog(false);
              setSelectedItem(null);
            }}
            onSuccess={() => {
              setShowEditDialog(false);
              setSelectedItem(null);
              fetchData();
            }}
          />
        ) : (
          <EditReminder
            reminder={selectedItem}
            isDialog={true}
            onClose={() => {
              setShowEditDialog(false);
              setSelectedItem(null);
            }}
            onSuccess={() => {
              setShowEditDialog(false);
              setSelectedItem(null);
              fetchData();
            }}
          />
        )}
      </Dialog>
    </Layout>
  );
};

export default PenaltyReminderSelector;